import React, { useEffect, useRef, useState } from "react";
import QrScannerLib from "qr-scanner";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector } from "react-redux";

function QrScanner({ open, onClose, onScanResult }) {
  const { selectedClientData } = useSelector((state) => state?.main);
  const AccentColor = selectedClientData?.AccentColor;

  const videoRef = useRef(null);
  const qrScannerRef = useRef(null);
  const [error, setError] = useState("");
  const [hasPermission, setHasPermission] = useState(false);
  const [tryAgain, setTryAgain] = useState(true);
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        initializeScanner();
      }, 300);
    } else {
      stopScanner();
    }

    return () => {
      stopScanner();
    };
  }, [open, hasPermission]);

  const initializeScanner = async () => {
    try {
      setError("");

      // Check if camera is supported
      if (!QrScannerLib.hasCamera()) {
        console.error("No camera found on this device");
        setError("No camera found on this device");
        return;
      }

      // Request camera permission
      const stream = await navigator.mediaDevices?.getUserMedia({ video: true });
      setHasPermission(true);
      // Stop the permission test stream
      stream.getTracks().forEach((track) => track.stop());

      if (videoRef.current) {
        // Initialize QR Scanner
        qrScannerRef.current = new QrScannerLib(
          videoRef.current,
          (result) => {
            onScanResult(result);
            stopScanner();
            onClose();
          },
          {
            returnDetailedScanResult: true,
            highlightScanRegion: true,
            highlightCodeOutline: true
          }
        );
        await qrScannerRef.current.start();
      }
    } catch (err) {
      console.error("Error initializing scanner:", err);
      if (tryAgain) {
        initializeScanner();
        setTryAgain(false);
      } else {
        if (err.name === "NotAllowedError") {
          setError("Camera permission denied. Please allow camera access to scan QR codes.");
        } else if (err.name === "NotFoundError") {
          setError("No camera found on this device.");
        } else if (err.name === "NotSupportedError") {
          setError("Camera is not supported on this device.");
        } else {
          setError("Failed to access camera. Please try again.");
        }
      }
    }
  };

  const stopScanner = () => {
    if (qrScannerRef.current) {
      qrScannerRef.current.stop();
      qrScannerRef.current.destroy();
      qrScannerRef.current = null;
    }
  };

  const handleClose = () => {
    stopScanner();
    onClose();
  };

  const requestPermissionAgain = async () => {
    setError("");
    await initializeScanner();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          maxHeight: "90vh"
        }
      }}>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1
        }}>
        <Typography variant="h6" fontWeight="bold">
          Scan QR Code
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        {error && (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Button
              variant="contained"
              onClick={requestPermissionAgain}
              sx={{
                backgroundColor: AccentColor,
                "&:hover": {
                  backgroundColor: AccentColor,
                  opacity: 0.9
                }
              }}>
              Try Again
            </Button>
          </Box>
        )}
        <Box>
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: "300px",
              borderRadius: "8px",
              overflow: "hidden",
              backgroundColor: "#000"
            }}>
            <video
              ref={videoRef}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover"
              }}
            />
          </Box>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2, textAlign: "center" }}>
            Position the QR code within the camera view to scan
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            borderColor: AccentColor,
            color: AccentColor,
            "&:hover": {
              borderColor: AccentColor,
              backgroundColor: `${AccentColor}10`
            }
          }}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default QrScanner;
